"use client";

import clsx from "clsx";
import NewsCard from "~/components/cards/NewsCard";
import Icon from "~/components/Icon/Icon";
import {
  type AcfMediaItemConnectionEdge,
  type ListNewsQuery,
} from "~/__generated__/graphql";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

const PAGE_SIZE = 6;

export default function NewsOverviewContent({
  newsData,
}: {
  newsData: ListNewsQuery;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const totalPages = Math.ceil(
    (newsData.articleTag?.pages?.edges.length ?? 0) / PAGE_SIZE,
  );
  const currentPage = parseInt(searchParams?.get("page") ?? "1");

  const news = newsData.articleTag?.pages?.edges.slice(
    PAGE_SIZE * (currentPage - 1),
    PAGE_SIZE * (currentPage - 1) + PAGE_SIZE,
  );

  const pages = [];

  // decide pagination start
  const pStart = currentPage > 2 ? currentPage - 2 : 1;
  // decide the amount of pages between current en pagination start
  const currentStartDiff = 5 - Math.abs(currentPage - pStart) - 1;
  // decide pagination end based on max page or current + 2 to make a total of 5 in pagination
  const pEnd =
    currentPage + currentStartDiff >= totalPages
      ? totalPages
      : currentPage + currentStartDiff;
  // if pEnd is close to max page total amount of pagination could be less then 5
  // this calculates how many it is missing
  const currentEndDiff = 2 - Math.abs(currentPage - pEnd);

  for (let i = pStart; i <= pEnd; i++) {
    pages.push(i);
  }

  // this adds the amount missing to the beginning
  if (currentEndDiff > 0) {
    for (let i = pStart - 1; i >= pStart - currentEndDiff; i--) {
      if (i > 0) pages.unshift(i);
    }
  }

  return (
    <>
      <div className="grid items-start gap-x-8 gap-y-10 sm:grid-cols-2 md:grid-cols-8 md:gap-y-14 lg:grid-cols-12">
        {/* Other news items */}
        {news && news.length > 0 ? (
          news.map((newsItem) => {
            return (
              <NewsCard
                key={newsItem.node.title}
                url={newsItem.node.uri ?? ""}
                title={newsItem.node.title}
                date={newsItem.node.date}
                highlightedImage={
                  newsItem.node?.featuredImage as AcfMediaItemConnectionEdge
                }
                image={
                  newsItem.node?.featuredImage as AcfMediaItemConnectionEdge
                }
                description={newsItem.node.articleFields?.description}
                itemType={newsData.articleTag?.name}
                isVideo={newsData.articleTag?.name === "video"}
                className="col-span-1 md:col-span-4"
              />
            );
          })
        ) : (
          // No results markup
          <div className="flex flex-col items-center justify-start gap-4 sm:col-span-2 md:col-span-8 md:gap-8 lg:col-span-8 lg:col-start-3">
            <h2 className="text-heading-5">
              Unfortunately, there are no news items matching the selected
              criteria.
            </h2>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center gap-4 md:gap-6">
          {pStart > 1 && (
            <button
              onClick={() => router.push(`${pathname}?page=1`)}
              className="translate-x-0 rotate-180 transition-transform hover:-translate-x-2"
              aria-label="Go to the first page"
            >
              <Icon
                type="hoverArrow"
                className="transition-color w-6 stroke-black hover:stroke-blue"
              />
            </button>
          )}
          <div className="flex gap-2 md:gap-4">
            {pages.map((page) => (
              <button
                className={clsx(
                  "transition-color px-2 text-body hover:text-blue md:px-0",
                  currentPage === page && "text-blue",
                )}
                onClick={() => router.push(`${pathname}?page=${page}`)}
                aria-label={`Go to page ${page}`}
                key={page}
              >
                {page}
              </button>
            ))}
          </div>
          {pEnd < totalPages && (
            <button
              onClick={() => router.push(`${pathname}?page=${totalPages}`)}
              className="translate-x-0 transition-transform hover:translate-x-2"
              aria-label="Go to the last page"
            >
              <Icon
                type="hoverArrow"
                className="transition-color w-6 stroke-black hover:stroke-blue"
              />
            </button>
          )}
        </div>
      )}
    </>
  );
}
